import {
  Box,
  CircularProgress,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { tierTitleKey } from "src/business/subscription/tierLabel";
import { formatPrice } from "src/Utils/number";
import { InfoCard } from "src/design";
import { Payment } from "src/business/payment/payment";
import { downloadProof } from "src/data/payment/queries/payment";
import { usePagination } from "src/helpers/usePagination";
import { TablePaginationBar } from "src/design/TablePaginationBar";
import {
  colorAccent,
  colorTextError,
  colorTextMuted,
  colorTextPrimary,
} from "src/design/tokens";
import { useProofDownload } from "../hooks/useProofDownload";
import { PaymentStatusChip } from "./PaymentStatusChip";

interface PaymentHistoryTableProps {
  payments: Payment[];
  restaurantId: string;
  sx?: SxProps;
}

const headerCellSx = { color: colorTextMuted, fontWeight: 600 } as const;

/**
 * Every payment ever declared, most recent first. Amounts render exactly as
 * stored — a confirmed payment keeps the price that applied on the day it was
 * declared, so nothing here is recomputed from the current catalog.
 */
export const PaymentHistoryTable = ({
  payments,
  restaurantId,
  sx,
}: PaymentHistoryTableProps) => {
  const { t, i18n } = useTranslation();
  const { download, downloadingId, errorKey } = useProofDownload();

  const ordered = [...payments].sort((a, b) =>
    dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
  );
  const { page, rowsPerPage, visibleRows, setPage, setRowsPerPage } =
    usePagination(ordered);

  return (
    <InfoCard
      title={t("payments.history.title")}
      subtitle={t("payments.history.subtitle")}
      icon={<ReceiptLongOutlinedIcon sx={{ color: colorAccent }} />}
      sx={sx}
    >
      {ordered.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontWeight: 600, color: colorTextPrimary }}>
            {t("payments.history.empty.title")}
          </Typography>
          <Typography variant="body2" sx={{ color: colorTextMuted }}>
            {t("payments.history.empty.message")}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, pl: 0 }}>
                    {t("payments.history.columns.date")}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("payments.history.columns.type")}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("payments.history.columns.tier")}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("payments.history.columns.duration")}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("payments.history.columns.amount")}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("payments.history.columns.status")}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t("payments.history.columns.proof")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell sx={{ pl: 0 }}>
                      {dayjs(payment.createdAt).format("MMM D, YYYY")}
                    </TableCell>
                    <TableCell>{t(`payments.type.${payment.type}`)}</TableCell>
                    <TableCell>
                      {t(tierTitleKey(payment.targetTier), {
                        defaultValue: payment.targetTier,
                      })}
                    </TableCell>
                    <TableCell>
                      {t(`payments.period.${payment.period}`)}
                      <Typography
                        variant="caption"
                        sx={{ display: "block", color: colorTextMuted }}
                      >
                        {t("payments.months", { count: payment.months })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {t("payments.amount", {
                        amount: formatPrice(payment.amount, i18n.language),
                      })}
                    </TableCell>
                    <TableCell>
                      <PaymentStatusChip status={payment.status} />
                      {payment.rejectionReason && (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: colorTextError }}
                        >
                          {t("payments.history.rejectionReason", {
                            reason: payment.rejectionReason,
                          })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("payments.history.download")}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={downloadingId === payment.id}
                            onClick={() =>
                              download(payment.id, () =>
                                downloadProof(restaurantId, payment.id),
                              )
                            }
                          >
                            {downloadingId === payment.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DownloadOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {errorKey && (
            <Typography variant="caption" sx={{ color: colorTextError }}>
              {t(errorKey)}
            </Typography>
          )}

          <TablePaginationBar
            count={ordered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </Box>
      )}
    </InfoCard>
  );
};
